import { useEffect, useState } from 'react';
import type { TrackingCarrier } from '@rosita/shared';
import { TRACKING_CARRIERS } from '@rosita/shared';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm hover:bg-admin-surface-alt disabled:opacity-50';

interface ShipOrderDialogProps {
  orderNumber: string;
  /** Rejections keep the dialog open so the admin can retry. */
  onConfirm: (carrier: TrackingCarrier, trackingNumber: string) => Promise<void>;
  onClose: () => void;
}

export function ShipOrderDialog({
  orderNumber,
  onConfirm,
  onClose,
}: ShipOrderDialogProps): JSX.Element {
  const [carrier, setCarrier] = useState<TrackingCarrier>('ctt');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Mirrors the Zod min(3) so the server never rejects on length.
  const valid = trackingNumber.trim().length >= 3;

  const submit = async (): Promise<void> => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onConfirm(carrier, trackingNumber.trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ship-order-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <form
        className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <h2 id="ship-order-dialog-title" className="mb-2 text-lg font-semibold">
          Expedir encomenda {orderNumber}
        </h2>
        <p className="mb-4 text-sm text-admin-text-soft">
          A cliente vai receber um email com o número de rastreio.
        </p>
        <div className="mb-5 space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-admin-text-soft">Transportadora</span>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value as TrackingCarrier)}
              disabled={busy}
              className={`${inputClass} w-full`}
            >
              {TRACKING_CARRIERS.map((c) => (
                <option key={c} value={c}>
                  {c.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-admin-text-soft">Número de rastreio</span>
            <input
              autoFocus
              value={trackingNumber}
              placeholder="Ex.: RR123456789PT"
              onChange={(e) => setTrackingNumber(e.target.value)}
              disabled={busy}
              className={`${inputClass} w-full`}
            />
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy} className={secondaryBtn}>
            Cancelar
          </button>
          <button type="submit" disabled={busy || !valid} className={primaryBtn}>
            {busy ? 'A expedir…' : 'Marcar como expedida'}
          </button>
        </div>
      </form>
    </div>
  );
}
